import { Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { useRef } from "react";

import type { TokenDefinition } from "../tokens/types";
import { CollectorPanel } from "./CollectorPanel";
import type { CollectorAnchor } from "./useTokenPalette";

export interface TokenCollectorPopoverProps {
  /** The parameterized token being configured; null keeps the popover closed. */
  definition: TokenDefinition | null;
  /** Pre-fills the collector for the edit-in-place flow. */
  initialValues?: Record<string, string>;
  /** Viewport coordinates the popover anchors to (clicked badge / drop point / clicked token). */
  anchor: CollectorAnchor | null;
  onSubmit: (params: Record<string, string>) => void;
  onClose: () => void;
}

/**
 * The anchored, modal-behaving popover that hosts the token collector. A virtual anchor lets it
 * position against raw viewport coordinates (immune to a transformed parent Dialog), and
 * `data-interactive-with-dialog` + Esc-stop keep dismissing the collector from also closing that
 * dialog. Shared by the palette (insert/drop) and the editor surfaces (edit-in-place).
 */
export const TokenCollectorPopover = ({
  definition,
  initialValues,
  anchor,
  onSubmit,
  onClose,
}: TokenCollectorPopoverProps) => {
  const anchorPointRef = useRef(anchor);
  anchorPointRef.current = anchor;
  const anchorRef = useRef({
    getBoundingClientRect: () => {
      const { x, y } = anchorPointRef.current ?? { x: 0, y: 0 };
      return new DOMRect(x, y, 0, 0);
    },
  });

  return (
    <Popover open={Boolean(definition)} onOpenChange={(open) => !open && onClose()} modal>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        className="w-80 p-0"
        align="start"
        data-interactive-with-dialog
        onEscapeKeyDown={(event) => event.stopPropagation()}
      >
        {definition && (
          <CollectorPanel definition={definition} initialValues={initialValues} onSubmit={onSubmit} onClose={onClose} />
        )}
      </PopoverContent>
    </Popover>
  );
};
